/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import type { FC } from 'react';
import React from 'react';
import { css } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { QuickSummaryPopup } from '@confluence/quick-summary/entry-points/QuickSummaryPopup.tsx';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../../Feed.tsx';

import type {
	FeedCardActionsMenuQuery as FeedCardActionsMenuQuery$data,
	FeedCardActionsMenuQueryVariables as FeedCardActionsMenuQuery$variables,
} from './__types__/FeedCardActionsMenuQuery.ts';
import { FeedCardStarAction } from './FeedCardStarAction/index.ts';
import { FeedCardWatchAction } from './FeedCardWatchAction.tsx';
import { FeedCardCopyLinkAction } from './FeedCardCopyLinkAction.tsx';
import { ActionButtonWrapper } from './ActionButtonStyles.tsx';
import { FeedAudioPlaylistButton } from './FeedAudioPlaylistButton.tsx';

const FeedCardActionsMenuQuery = graphql`
	query FeedCardActionsMenuQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			metadata {
				currentuser {
					favourited {
						isFavourite
					}
				}
			}
		}
	}
`;
const actionsWrapperStyle = css({
	display: 'flex',
	alignItems: 'center',
});

const QuickSummaryButtonContainer = xcss({
	minWidth: '118px',
});

const QuickSummaryButtonStyles = {
	border: `1.5px solid ${token('color.border')}`,
	borderRadius: token('radius.small'),
	padding: '3px 7px',
};

const ListItemWrapperStyles = xcss({
	listStyleType: 'none',
	margin: '0',
	padding: '0',
});

const ActionButtonWrapperStyles = xcss({
	marginLeft: 'space.100',
});

type Props = {
	contentTitle: string;
	contentId: string;
	contentUrl: string;
	spaceKey?: string;
	contentType: 'page' | 'blogpost';
	setIsActionMenuOpen: (open: boolean) => void;
	hasCardBeenHovered: boolean;
	source: string;
	lastModifiedDate: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
};

export const FeedCardActions: FC<Props> = ({
	contentId,
	contentUrl,
	contentType,
	spaceKey,
	setIsActionMenuOpen,
	hasCardBeenHovered,
	source,
	lastModifiedDate,
	popularFeedTimeGranularity,
	followingFeedSortby,
	contentTitle,
}) => {
	const { data, error } = useQuery<
		FeedCardActionsMenuQuery$data,
		FeedCardActionsMenuQuery$variables
	>(FeedCardActionsMenuQuery, {
		variables: { contentId },
	});
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	const renderStarAction = () => {
		if (error) {
			return <ErrorDisplay error={error} />;
		}

		const isStarred = !!data?.singleContent?.metadata?.currentuser?.favourited?.isFavourite;
		return <FeedCardStarAction contentId={contentId} isStarred={isStarred} />;
	};

	return (
		<div css={actionsWrapperStyle} data-testid="feed-card-actions">
			<Flex as="ul" alignItems="center">
				{fg('confluence_audio_briefings_v3') && (
					<Box xcss={ListItemWrapperStyles} as="li">
						<ActionButtonWrapper>
							<FeedAudioPlaylistButton
								contentType={contentType}
								contentId={contentId}
								contentTitle={contentTitle}
							/>
						</ActionButtonWrapper>
					</Box>
				)}
				<Box xcss={ListItemWrapperStyles} as="li">
					<FeedCardCopyLinkAction
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						source={source}
						popularFeedTimeGranularity={popularFeedTimeGranularity}
						followingFeedSortby={followingFeedSortby}
						iconColor={token('color.icon.subtlest')}
					/>
				</Box>
				<Box xcss={ListItemWrapperStyles} as="li">
					<FeedCardWatchAction
						contentId={contentId}
						setIsMenuOpen={setIsActionMenuOpen}
						hasCardBeenHovered={hasCardBeenHovered}
						contentType={contentType}
						popularFeedTimeGranularity={popularFeedTimeGranularity}
						followingFeedSortby={followingFeedSortby}
					/>
				</Box>
				<Box xcss={ListItemWrapperStyles} as="li">
					{renderStarAction()}
				</Box>

				{isAIEnabledForContent && (
					<Box xcss={[ListItemWrapperStyles, ActionButtonWrapperStyles]} as="li">
						<ActionButtonWrapper>
							<Box xcss={QuickSummaryButtonContainer}>
								<QuickSummaryPopup
									contentPath={contentUrl}
									contentId={contentId}
									spaceKey={spaceKey}
									contentType={contentType}
									source={source}
									lastModifiedDate={lastModifiedDate}
									setIsFeedMenuOpen={setIsActionMenuOpen}
									buttonStyles={QuickSummaryButtonStyles}
									popularFeedTimeGranularity={popularFeedTimeGranularity}
									followingFeedSortby={followingFeedSortby}
								/>
							</Box>
						</ActionButtonWrapper>
					</Box>
				)}
			</Flex>
		</div>
	);
};
